/**
 * src/pages/WhereDidWeComeFrom/whereDidWeComeFromData.ts
 * Rigorous astrophysics, cosmochemistry, abiogenesis, and paleoanthropology dataset
 * with epistemological tags and verified scientific citations.
 */

import type { TimelineEvent, ExpandItem, SourceRef, EvidenceLevel } from "@/lib/originBeyond/types";

/* ==========================================================================
   1. Real Scholarly Citations (7 to 10 verified sources)
   ========================================================================== */
export const ORIGIN_SOURCES: SourceRef[] = [
  {
    id: 1,
    citation: "Planck Collaboration (Aghanim, N. et al.). Planck 2018 results. VI. Cosmological parameters. Astronomy & Astrophysics 641, A6 (2020). Baseline Lambda-CDM age of 13.797 ± 0.023 billion years.",
    url: "https://arxiv.org/abs/1807.06209",
  },
  {
    id: 2,
    citation: "Burbidge, E. M., Burbidge, G. R., Fowler, W. A., & Hoyle, F. Synthesis of the Elements in Stars (B2FH). Reviews of Modern Physics 29, 547 (1957). Stellar nucleosynthesis foundation.",
    url: "https://doi.org/10.1103/RevModPhys.29.547",
  },
  {
    id: 3,
    citation: "Abbott, B. P. et al. (LIGO Scientific & Virgo Collaborations). Multi-messenger Observations of a Binary Neutron Star Merger (GW170817 / AT 2017gfo). The Astrophysical Journal Letters 848, L12 (2017). Heavy element r-process kilonova confirmation.",
    url: "https://doi.org/10.3847/2041-8213/aa91c9",
  },
  {
    id: 4,
    citation: "Miller, S. L. A Production of Amino Acids Under Possible Primitive Earth Conditions. Science 117, 528–529 (1953). Prebiotic organic chemistry simulation.",
    url: "https://doi.org/10.1126/science.117.3046.528",
  },
  {
    id: 5,
    citation: "Martin, W., Baross, J., Kelley, D., & Russell, M. J. Hydrothermal vents and the origin of life. Nature Reviews Microbiology 6, 805–814 (2008). Chemiosmosis at alkaline submarine vents.",
    url: "https://doi.org/10.1038/nrmicro1991",
  },
  {
    id: 6,
    citation: "Gilbert, W. Origin of life: The RNA world. Nature 319, 618 (1986). The catalytic and genetic dual role of prebiotic ribonucleic acid.",
    url: "https://doi.org/10.1038/319618a0",
  },
  {
    id: 7,
    citation: "Hublin, J.-J. et al. New fossils from Jebel Irhoud, Morocco and the pan-African origin of Homo sapiens. Nature 546, 289–292 (2017). Earliest known Homo sapiens remains dated to ~300,000 years ago.",
    url: "https://doi.org/10.1038/nature22336",
  },
  {
    id: 8,
    citation: "Green, R. E. et al. A Draft Sequence of the Neandertal Genome. Science 328, 710–722 (2010). Genomic evidence for ancient hominin admixture and gene flow.",
    url: "https://doi.org/10.1126/science.1188021",
  },
  {
    id: 9,
    citation: "Smithsonian Institution Human Origins Program. What does it mean to be human? Evidence of human evolution and the hominin bush.",
    url: "https://humanorigins.si.edu",
  },
  {
    id: 10,
    citation: "NASA Astrobiology Institute. Understanding the emergence of life in planetary environments and cosmic contexts.",
    url: "https://astrobiology.nasa.gov",
  },
];

/* ==========================================================================
   2. Cosmic & Terrestrial Deep Timeline (13.8 Billion Years)
   Logarithmically spaced positions [0.0 ... 1.0]
   ========================================================================== */
export const COSMIC_TIMELINE: TimelineEvent[] = [
  {
    id: "t-bigbang",
    time: "t = 0 to 10⁻³² s",
    position: 0.0,
    title: "The Big Bang & Cosmic Inflation",
    level: "research",
    refs: [1],
    body: "Cosmic expansion began approximately 13.8 billion years ago from a state of extreme density and temperature. While observational cosmology maps the universe with remarkable precision from fractions of a second onward, science currently has no empirical data for what—if anything—preceded the expansion or why it initiated. Cosmic inflation is our leading physical model for cosmic flatness and horizon uniformity, but remains an active research area.",
  },
  {
    id: "t-nuclei",
    time: "t ≈ 3 to 20 minutes",
    position: 0.1,
    title: "Big Bang Nucleosynthesis",
    level: "established",
    refs: [1, 2],
    body: "As the infant cosmos expanded and cooled below roughly one billion Kelvin, protons and neutrons fused to forge primordial light nuclei: approximately 75% hydrogen, 25% helium-4, and trace fractions of deuterium, helium-3, and lithium-7. The precise primordial abundances measured today match cosmological calculations to high accuracy.",
  },
  {
    id: "t-recombination",
    time: "t ≈ 380,000 years",
    position: 0.22,
    title: "Recombination & Cosmic Microwave Background",
    level: "established",
    refs: [1],
    body: "Temperatures dropped to about 3,000 Kelvin, allowing free electrons to bind with nuclei to form the first neutral atoms. The universe turned transparent to photons, releasing the Cosmic Microwave Background (CMB)—an ancient relic snapshot of density ripples that seeded every modern supercluster and void.",
  },
  {
    id: "t-first-stars",
    time: "t ≈ 100–200 million years",
    position: 0.35,
    title: "Cosmic Dawn & Population III Stars",
    level: "research",
    refs: [1, 2],
    body: "Gravity pulled pristine hydrogen and helium into primordial dark matter halos, igniting the first generation of massive, metal-free stars (Population III). Their powerful ultraviolet radiation reionized the intergalactic medium and their core fusion created the universe's first carbon, oxygen, and iron.",
  },
  {
    id: "t-galaxies",
    time: "t ≈ 400 million to 1 Gyr",
    position: 0.46,
    title: "First Galaxies & Cosmic Web Assembly",
    level: "established",
    refs: [1],
    body: "Protogalactic fragments merged along cosmic filamentary web pathways. Deep space observatories like JWST and Hubble observe massive, luminous galaxies forming significantly earlier and more efficiently than simple models predicted, continuously enriching the interstellar gas with synthesized elements.",
  },
  {
    id: "t-solarsystem",
    time: "4.567 billion years ago",
    position: 0.6,
    title: "Formation of the Sun & Solar Nebula",
    level: "established",
    refs: [2],
    body: "A localized molecular cloud collapsed in our spiral arm, triggered in part by nearby ancient supernova shockwaves that seeded short-lived radionuclides like aluminum-26. The central mass ignited our Sun, while the remaining protoplanetary disk condensed into planetesimals.",
  },
  {
    id: "t-earth-moon",
    time: "≈ 4.54 to 4.51 billion years ago",
    position: 0.68,
    title: "Accretion of Earth & the Giant Moon Impact",
    level: "established",
    refs: [10],
    body: "Earth accreted through high-energy collisions of rocky bodies. Shortly after formation, a Mars-sized protoplanet ('Theia') collided with the proto-Earth; debris ejected into orbit rapidly coalesced to form the Moon, stabilizing Earth's axial tilt and moderating long-term climate oscillations.",
  },
  {
    id: "t-life-emergence",
    time: "≥ 3.5 to 3.8 billion years ago",
    position: 0.77,
    title: "Earliest Evidence of Terrestrial Life",
    level: "established",
    refs: [10],
    body: "Stromatolites and biogenic microstructures in Western Australia and Greenland demonstrate that self-replicating, cellular microbial life inhabited shallow waters and geothermal vents at least 3.5 billion years ago (with contested isotopic hints extending past 3.8 billion years).",
  },
  {
    id: "t-oxygenation",
    time: "≈ 2.4 to 2.1 billion years ago",
    position: 0.84,
    title: "The Great Oxidation Event",
    level: "established",
    refs: [10],
    body: "Photosynthetic cyanobacteria began producing free molecular oxygen in vast quantities. After saturating oceanic iron buffers (producing banded iron formations), oxygen accumulated in the atmosphere, creating the protective ozone layer while triggering severe global glaciation.",
  },
  {
    id: "t-animals",
    time: "≈ 541 to 65 million years ago",
    position: 0.92,
    title: "Cambrian Explosion & Diversification of Animals",
    level: "established",
    refs: [9],
    body: "Bilaterian animals, mineralized shells, complex nervous systems, and sensory eyes rapidly diversified during the Cambrian period. Over subsequent epochs, vertebrates colonized land, plants transformed continents, and several mass extinction boundaries reset ecosystem architectures.",
  },
  {
    id: "t-homosapiens",
    time: "≈ 300,000 years ago to Present",
    position: 1.0,
    title: "Anatomically Modern Humans (Homo sapiens)",
    level: "established",
    refs: [7, 9],
    body: "Anatomically modern human fossils appear in the African record by roughly 300,000 years ago (such as Jebel Irhoud, Morocco). Across hundreds of millennia of climatic fluctuation, our lineage developed complex symbolic material culture, language, and planet-spanning technological networks.",
  },
];

/* ==========================================================================
   3. Stardust: Element Origin Data (Interactive Periodic Grid)
   Origin categories:
   - "bigbang" (Big Bang fusion)
   - "dyingstars" (Dying low-mass stars / AGB)
   - "supernovae" (Massive exploding stars & core collapse)
   - "mergers" (Colliding neutron stars / r-process kilonovae)
   - "cosmicrays" (Cosmic ray spallation)
   - "synthetic" (Synthesized in laboratories)
   ========================================================================== */
export type ElementOriginId =
  | "bigbang"
  | "dyingstars"
  | "supernovae"
  | "mergers"
  | "cosmicrays"
  | "synthetic";

export interface ElementOriginCategory {
  id: ElementOriginId;
  label: string;
  short: string;
  colorName: string;
  bgClass: string;
  borderClass: string;
  textClass: string;
  description: string;
}

export const ELEMENT_ORIGIN_CATEGORIES: Record<ElementOriginId, ElementOriginCategory> = {
  bigbang: {
    id: "bigbang",
    label: "Big Bang Fusion",
    short: "Big Bang",
    colorName: "cyan",
    bgClass: "bg-cyan-500/20",
    borderClass: "border-cyan-400/50",
    textClass: "text-cyan-300",
    description: "Forged during the first twenty minutes of the expanding universe before stars existed.",
  },
  dyingstars: {
    id: "dyingstars",
    label: "Dying Low-Mass Stars",
    short: "Dying Stars",
    colorName: "amber",
    bgClass: "bg-amber-500/20",
    borderClass: "border-amber-400/50",
    textClass: "text-amber-300",
    description: "Asymptotic Giant Branch (AGB) stars slow-neutron capture (s-process) shedding stellar winds.",
  },
  supernovae: {
    id: "supernovae",
    label: "Exploding Massive Stars",
    short: "Supernovae",
    colorName: "emerald",
    bgClass: "bg-emerald-500/20",
    borderClass: "border-emerald-400/50",
    textClass: "text-emerald-300",
    description: "Core-collapse supernovae and thermonuclear Type Ia events blasting energetic blast fronts.",
  },
  mergers: {
    id: "mergers",
    label: "Neutron Star Mergers",
    short: "Kilonovae",
    colorName: "violet",
    bgClass: "bg-purple-500/20",
    borderClass: "border-purple-400/50",
    textClass: "text-purple-300",
    description: "Rapid neutron-capture (r-process) in the relativistic debris of colliding stellar remnants.",
  },
  cosmicrays: {
    id: "cosmicrays",
    label: "Cosmic Ray Spallation",
    short: "Cosmic Rays",
    colorName: "blue",
    bgClass: "bg-blue-500/20",
    borderClass: "border-blue-400/50",
    textClass: "text-blue-300",
    description: "Relativistic protons and alpha particles shattering heavier interstellar atoms.",
  },
  synthetic: {
    id: "synthetic",
    label: "Laboratory Synthesis",
    short: "Synthetic",
    colorName: "rose",
    bgClass: "bg-rose-500/20",
    borderClass: "border-rose-400/50",
    textClass: "text-rose-300",
    description: "Highly unstable transuranic elements produced artificially by particle accelerators.",
  },
};

export interface ChemicalElement {
  z: number;
  symbol: string;
  name: string;
  origin: ElementOriginId;
  row: number; // 1 to 7
  col: number; // 1 to 18
  notableFact?: string;
  biologicalRole?: string;
}

// Curated full dataset for elements Z=1 through Z=92 (Natural Elements) + representative synthetics
export const PERIODIC_ELEMENTS: ChemicalElement[] = [
  // Period 1
  { z: 1, symbol: "H", name: "Hydrogen", origin: "bigbang", row: 1, col: 1, notableFact: "Accounted for ~75% of baryonic mass at the Big Bang; makes up ~63% of the atoms in the human body.", biologicalRole: "Water, organic polymers, cellular acids" },
  { z: 2, symbol: "He", name: "Helium", origin: "bigbang", row: 1, col: 18, notableFact: "Second most abundant element in the cosmos; first discovered in solar spectral lines.", biologicalRole: "Inert noble gas" },

  // Period 2
  { z: 3, symbol: "Li", name: "Lithium", origin: "bigbang", row: 2, col: 1, notableFact: "Primordial lithium is observed in ancient halo stars, though with a persistent cosmological discrepancy.", biologicalRole: "Trace neurochemical modulation" },
  { z: 4, symbol: "Be", name: "Beryllium", origin: "cosmicrays", row: 2, col: 2, notableFact: "Too unstable to survive stellar fusion cores; produced when cosmic rays shatter carbon and oxygen.", biologicalRole: "Non-essential / toxic in free form" },
  { z: 5, symbol: "B", name: "Boron", origin: "cosmicrays", row: 2, col: 13, notableFact: "Formed almost entirely via cosmic ray spallation in the cold interstellar medium.", biologicalRole: "Trace micronutrient in plants" },
  { z: 6, symbol: "C", name: "Carbon", origin: "dyingstars", row: 2, col: 14, notableFact: "The foundational backbone of terrestrial biochemistry, forged via the triple-alpha process in giant stars.", biologicalRole: "Backbone of all organic molecules" },
  { z: 7, symbol: "N", name: "Nitrogen", origin: "dyingstars", row: 2, col: 15, notableFact: "Primary constituent of Earth's atmosphere (~78%); synthesized via the stellar CNO fusion cycle.", biologicalRole: "Amino acids, RNA, and DNA bases" },
  { z: 8, symbol: "O", name: "Oxygen", origin: "supernovae", row: 2, col: 16, notableFact: "Most abundant element in the human body by mass (~65%), blown across space by core-collapse supernovae.", biologicalRole: "Cellular respiration and water" },
  { z: 9, symbol: "F", name: "Fluorine", origin: "dyingstars", row: 2, col: 17, notableFact: "Synthesized in pulsating asymptotic giant branch (AGB) stars and neutrino spallation.", biologicalRole: "Enamel hardening (fluorapatite)" },
  { z: 10, symbol: "Ne", name: "Neon", origin: "supernovae", row: 2, col: 18, notableFact: "Abundant in massive stars during neon-burning phases prior to core collapse.", biologicalRole: "Inert noble gas" },

  // Period 3
  { z: 11, symbol: "Na", name: "Sodium", origin: "supernovae", row: 3, col: 1, notableFact: "Ejected primarily by core-collapse supernovae; essential for nerve impulse transmission.", biologicalRole: "Electrolyte balance and neural firing" },
  { z: 12, symbol: "Mg", name: "Magnesium", origin: "supernovae", row: 3, col: 2, notableFact: "Crucial coordinating ion at the physical core of photosynthetic chlorophyll molecules.", biologicalRole: "ATP enzyme catalysis and DNA replication" },
  { z: 13, symbol: "Al", name: "Aluminum", origin: "supernovae", row: 3, col: 13, notableFact: "Most abundant metallic element in Earth's crust; radioactive Al-26 melted early protoplanetary cores.", biologicalRole: "Non-biological in organisms" },
  { z: 14, symbol: "Si", name: "Silicon", origin: "supernovae", row: 3, col: 14, notableFact: "Forms the silicate mineral mantle of rocky terrestrial planets; fused during stellar silicon-burning.", biologicalRole: "Skeletal structures in diatoms and sponges" },
  { z: 15, symbol: "P", name: "Phosphorus", origin: "supernovae", row: 3, col: 15, notableFact: "Critical structural link in the RNA/DNA sugar-phosphate backbone and ATP energetic currency.", biologicalRole: "Nucleic acids, cell membranes, ATP" },
  { z: 16, symbol: "S", name: "Sulfur", origin: "supernovae", row: 3, col: 16, notableFact: "Forms covalent disulfide bridges that lock 3D protein geometry in place.", biologicalRole: "Methionine, cysteine amino acids" },
  { z: 17, symbol: "Cl", name: "Chlorine", origin: "supernovae", row: 3, col: 17, notableFact: "Dominant anion in extracellular fluids, vital for electrical potentials across cell membranes.", biologicalRole: "Osmotic balance, stomach acid (HCl)" },
  { z: 18, symbol: "Ar", name: "Argon", origin: "supernovae", row: 3, col: 18, notableFact: "Atmospheric argon-40 on Earth is largely the decay product of crustal potassium-40.", biologicalRole: "Inert noble gas" },

  // Period 4
  { z: 19, symbol: "K", name: "Potassium", origin: "supernovae", row: 4, col: 1, notableFact: "Natural radioactive isotope K-40 contributes a measurable fraction of our internal human radiation.", biologicalRole: "Resting membrane potential in neurons" },
  { z: 20, symbol: "Ca", name: "Calcium", origin: "supernovae", row: 4, col: 2, notableFact: "Forged in oxygen-silicon fusion layers; building block of endoskeletons and calcium signalling.", biologicalRole: "Bone density, muscular contraction" },
  { z: 21, symbol: "Sc", name: "Scandium", origin: "supernovae", row: 4, col: 3, notableFact: "Transition metal forged in core-collapse shockwaves.", biologicalRole: "Trace element" },
  { z: 22, symbol: "Ti", name: "Titanium", origin: "supernovae", row: 4, col: 4, notableFact: "Extremely corrosion-resistant metal synthesized in advanced stellar burning zones.", biologicalRole: "Biocompatible implant material" },
  { z: 23, symbol: "V", name: "Vanadium", origin: "supernovae", row: 4, col: 5, notableFact: "Used by marine sea squirts (ascidians) to bind oxygen in specialized blood cells.", biologicalRole: "Enzyme cofactor in select sea organisms" },
  { z: 24, symbol: "Cr", name: "Chromium", origin: "supernovae", row: 4, col: 6, notableFact: "Dispersed into the interstellar medium via energetic supernova blast waves.", biologicalRole: "Trace glucose metabolic cofactor" },
  { z: 25, symbol: "Mn", name: "Manganese", origin: "supernovae", row: 4, col: 7, notableFact: "Anchors the oxygen-evolving complex in photosystem II, enabling green plants to split water.", biologicalRole: "Photosynthetic water splitting" },
  { z: 26, symbol: "Fe", name: "Iron", origin: "supernovae", row: 4, col: 8, notableFact: "The ultimate nuclear thermodynamic endpoint of exothermic fusion; powers hemoglobin in human blood.", biologicalRole: "Oxygen binding in hemoglobin and cytochromes" },
  { z: 27, symbol: "Co", name: "Cobalt", origin: "supernovae", row: 4, col: 9, notableFact: "Essential catalytic metal center inside Vitamin B12 (cobalamin).", biologicalRole: "Vitamin B12, red blood cell synthesis" },
  { z: 28, symbol: "Ni", name: "Nickel", origin: "supernovae", row: 4, col: 10, notableFact: "Major component of Earth's dense metallic core alongside iron.", biologicalRole: "Urease enzymes in microorganisms" },
  { z: 29, symbol: "Cu", name: "Copper", origin: "supernovae", row: 4, col: 11, notableFact: "Powers hemocyanin, the copper-based oxygen carrier that gives cephalopods blue blood.", biologicalRole: "Electron transport, cytochrome c oxidase" },
  { z: 30, symbol: "Zn", name: "Zinc", origin: "supernovae", row: 4, col: 12, notableFact: "Structural anchor for zinc finger proteins that regulate genetic transcription.", biologicalRole: "Zinc-finger DNA-binding transcription" },
  { z: 31, symbol: "Ga", name: "Gallium", origin: "supernovae", row: 4, col: 13, notableFact: "Melts in human hands; used in high-efficiency astrophysical semiconductor sensors.", biologicalRole: "Non-biological" },
  { z: 32, symbol: "Ge", name: "Germanium", origin: "supernovae", row: 4, col: 14, notableFact: "Early semiconductor material forged during deep stellar burning stages.", biologicalRole: "Non-biological" },
  { z: 33, symbol: "As", name: "Arsenic", origin: "dyingstars", row: 4, col: 15, notableFact: "Formed via slow neutron captures in aged low-to-intermediate-mass stars.", biologicalRole: "Toxic; utilized by niche extremophiles" },
  { z: 34, symbol: "Se", name: "Selenium", origin: "supernovae", row: 4, col: 16, notableFact: "Incorporated into selenocysteine, often called the 21st amino acid of biology.", biologicalRole: "Antioxidant enzymes (glutathione peroxidase)" },
  { z: 35, symbol: "Br", name: "Bromine", origin: "dyingstars", row: 4, col: 17, notableFact: "Recently recognized as essential for tissue development in animal collagen IV.", biologicalRole: "Collagen IV scaffold crosslinking" },
  { z: 36, symbol: "Kr", name: "Krypton", origin: "dyingstars", row: 4, col: 18, notableFact: "Noble gas produced by slow neutron captures inside asymptotic giant branch stars.", biologicalRole: "Inert noble gas" },

  // Period 5 Selection
  { z: 37, symbol: "Rb", name: "Rubidium", origin: "dyingstars", row: 5, col: 1, notableFact: "Used in optical clocks and laser-cooling atomic traps.", biologicalRole: "Trace ion" },
  { z: 38, symbol: "Sr", name: "Strontium", origin: "mergers", row: 5, col: 2, notableFact: "Directly identified in the spectroscopic kilonova glow of merging neutron star GW170817.", biologicalRole: "Bone mineral analog" },
  { z: 47, symbol: "Ag", name: "Silver", origin: "mergers", row: 5, col: 11, notableFact: "Formed in rapid neutron capture conditions during relativistic neutron star collisions.", biologicalRole: "Antimicrobial surface properties" },
  { z: 50, symbol: "Sn", name: "Tin", origin: "dyingstars", row: 5, col: 14, notableFact: "Forged primarily via s-process in dying red giant stars; allowed human Bronze Age tools.", biologicalRole: "Trace element in some animals" },
  { z: 53, symbol: "I", name: "Iodine", origin: "mergers", row: 5, col: 17, notableFact: "Heaviest element known to be universally required by human biology (thyroid hormone thyroxine).", biologicalRole: "Thyroid hormones T3 and T4 synthesis" },

  // Period 6 Selection (Gold, Platinum, Heavy Metals)
  { z: 78, symbol: "Pt", name: "Platinum", origin: "mergers", row: 6, col: 10, notableFact: "Synthesized almost exclusively in extreme r-process conditions during binary neutron star mergers.", biologicalRole: "Chemotherapeutic platinum coordination" },
  { z: 79, symbol: "Au", name: "Gold", origin: "mergers", row: 6, col: 11, notableFact: "Every gram of gold in wedding rings and electronics was forged when superdense neutron star cores collided.", biologicalRole: "Chemically unreactive in biology" },
  { z: 80, symbol: "Hg", name: "Mercury", origin: "mergers", row: 6, col: 12, notableFact: "Liquid metal forged in dense neutron star merger winds.", biologicalRole: "Neurotoxic heavy metal" },
  { z: 82, symbol: "Pb", name: "Lead", origin: "dyingstars", row: 6, col: 14, notableFact: "The ultimate stable decay destination for uranium and thorium decay chains.", biologicalRole: "Toxic heavy metal" },

  // Actinides
  { z: 90, symbol: "Th", name: "Thorium", origin: "mergers", row: 7, col: 4, notableFact: "Long-lived radioactive actinide whose deep decay keeps Earth's outer iron core molten and churning.", biologicalRole: "Geothermal planet heating" },
  { z: 92, symbol: "U", name: "Uranium", origin: "mergers", row: 7, col: 6, notableFact: "Heaviest primordial element. Its slow radioactive decay produces roughly half of Earth's internal geothermal heat.", biologicalRole: "Geothermal driver of planetary geodynamics" },
];

/* ==========================================================================
   4. How Life May Have Begun: Origin-of-Life Hypotheses
   Strictly labelled with epistemological distinctions and footnotes
   ========================================================================== */
export const ORIGIN_OF_LIFE_HYPOTHESES: ExpandItem[] = [
  {
    id: "vent-hypothesis",
    title: "Alkaline Hydrothermal Vents & Deep-Sea Chemiosmosis",
    level: "research",
    refs: [5, 10],
    summary: "Proton gradients and iron-sulfur minerals in porous deep-sea chimneys.",
    body: "This model proposes that life began not at the sunlit ocean surface, but deep on the ocean floor within porous, alkaline hydrothermal mounds. In early Hadean oceans, warm, alkaline vent fluids met cooler, acidic seawater across thin catalytic iron-sulfur mineral membranes. This natural pH difference produced continuous proton gradients across microscopic pores—a physical precursor that closely mirrors how modern cellular membranes use ATP synthase to power metabolism. While laboratory recreations produce organic polymers, transitioning from mineral pores to free-living cell membranes remains unresolved.",
  },
  {
    id: "rna-world",
    title: "The RNA World Hypothesis",
    level: "research",
    refs: [6, 10],
    summary: "Ribonucleic acid acting simultaneously as genetic data and catalytic enzyme.",
    body: "Modern biology relies on an interdependent molecular loop: DNA stores genetic sequences, but requires protein enzymes to replicate, while proteins execute catalysis but require DNA templates. The RNA World hypothesis proposes that ribonucleic acid (RNA) preceded both, because RNA can store hereditary information while folding into functional enzymes (ribozymes). The physical catalytic core of the modern cellular ribosome is a ribozyme, providing strong biochemical evidence. However, synthesizing complex ribose and nucleotide chains under realistic prebiotic conditions without guided intervention remains a formidable research challenge.",
  },
  {
    id: "primordial-soup",
    title: "Prebiotic Broth & Atmospheric Chemistry (Miller–Urey)",
    level: "research",
    refs: [4],
    summary: "Lightning and ultraviolet radiation acting on early atmospheric gases.",
    body: "In 1953, Stanley Miller and Harold Urey demonstrated that passing electrical sparks through a reducing mixture of methane, ammonia, hydrogen, and water vapor readily yields over twenty amino acids and organic building blocks. Subsequent geochemical evidence suggests Earth's early atmosphere was less reducing than Miller assumed (composed mostly of CO₂ and N₂), which reduces amino acid yields. Modified contemporary experiments combining atmospheric gases with ultraviolet sunlight and volcanic lightning continue to confirm that diverse organic precursors assemble readily under varied primitive conditions.",
  },
  {
    id: "mineral-clay",
    title: "Clay Templates & Mineral Surface Catalysis",
    level: "research",
    refs: [10],
    summary: "Layered montmorillonite clays organizing and polymerizing monomers.",
    body: "Mineral surfaces—particularly layered phyllosilicate clays like montmorillonite—possess charged crystal lattices capable of adsorbing organic molecules from dilute water. Experiments confirm that montmorillonite clay accelerates the self-assembly of lipid vesicles (primitive protocells) and catalyzes the polymerization of activated nucleotides into longer RNA strands. Minerals may have served as protective scaffolds, concentrating delicate organic chemicals against ultraviolet degradation while ordering their stereochemistry.",
  },
  {
    id: "panspermia",
    title: "Panspermia: Interplanetary Seeding",
    level: "speculative",
    refs: [10],
    summary: "Organic precursors or dormant spores transported by comets and meteorites.",
    body: "Carbonaceous meteorites (such as the Murchison meteorite) contain abundant extraterrestrial amino acids, nucleobases, and organic compounds, confirming that complex biochemical precursors are forged throughout the solar nebula. Panspermia hypothesizes that prebiotic compounds—or even viable microbial spores—were delivered to early Earth via cometary impacts. While interstellar transport of organic building blocks is empirically established, panspermia does not solve how life originated from non-living matter; it merely shifts the initial abiogenesis event to another cosmic environment.",
  },
];

/* ==========================================================================
   5. Becoming Human: The Hominin Evolutionary Bush
   ========================================================================== */
export interface HomininStage {
  id: string;
  timeWindow: string;
  species: string;
  significance: string;
  level: EvidenceLevel;
  refs: number[];
  traits: string[];
}

export const HOMININ_STAGES: HomininStage[] = [
  {
    id: "h-chimp-split",
    timeWindow: "≈ 6 to 8 million years ago",
    species: "Last Common Ancestor (Pan / Homo Split)",
    significance: "Genetic clock divergence between lineages leading to humans and modern chimpanzees.",
    level: "research",
    refs: [9],
    traits: [
      "Molecular divergence date inferred from genomic sequencing",
      "Fossils near the split include Sahelanthropus and Orrorin",
      "Facultative bipedalism emerging in mixed woodland landscapes",
    ],
  },
  {
    id: "h-australopithecus",
    timeWindow: "≈ 4.2 to 2.0 million years ago",
    species: "Australopithecus (e.g., A. afarensis / 'Lucy')",
    significance: "Habitual upright bipedalism proven by knee morphology and the Laetoli volcanic ash footprints.",
    level: "established",
    refs: [9],
    traits: [
      "Pelvic and femoral anatomy adapted for efficient upright walking",
      "Brain volume approximately 400–500 cm³, similar to modern chimpanzees",
      "Pronounced sexual dimorphism and retention of climbing adaptations",
    ],
  },
  {
    id: "h-early-homo",
    timeWindow: "≈ 2.8 to 1.5 million years ago",
    species: "Homo habilis & Early Oldowan Stone Toolmakers",
    significance: "Emergence of the genus Homo with enlarged cranial capacities and systematic flaked stone tool use.",
    level: "established",
    refs: [9],
    traits: [
      "Oldowan lithic core reduction technology for marrow access",
      "Cranial expansion to roughly 600–700 cm³",
      "Dietary shift toward animal fats and higher-calorie foraging",
    ],
  },
  {
    id: "h-erectus",
    timeWindow: "≈ 1.9 million to 200,000 years ago",
    species: "Homo erectus / ergaster",
    significance: "First global hominin dispersal across Eurasia; Acheulean handaxes and controlled use of fire.",
    level: "established",
    refs: [9],
    traits: [
      "Modern human body proportions optimized for endurance running",
      "Cranial capacity expanding to 900–1,100 cm³",
      "Evidence of controlled fire use at archaeological hearth sites (dates debated)",
    ],
  },
  {
    id: "h-neanderthal",
    timeWindow: "≈ 400,000 to 40,000 years ago",
    species: "Neanderthals & Denisovans (Archaic Relatives)",
    significance: "Cold-adapted sister lineages across Eurasia; genome sequencing proves interbreeding with early modern humans.",
    level: "established",
    refs: [8, 9],
    traits: [
      "Large brain volume (1,400–1,600 cm³) and robust skeletal frame",
      "Mousterian tool technology, pigment use, and intentional burials",
      "Ancient DNA proves 1–2% Neanderthal ancestry in present-day non-African populations",
    ],
  },
  {
    id: "h-sapiens",
    timeWindow: "≈ 300,000 years ago to Present",
    species: "Homo sapiens (Modern Humans)",
    significance: "Anatomically modern humans emerge in Africa (Jebel Irhoud) with rapid symbolic, artistic, and social complexity.",
    level: "established",
    refs: [7, 8, 9],
    traits: [
      "Globular cranium, distinct chin, and gracile skeletal framework",
      "Symbolic beadwork, representational cave art, and composite tools",
      "Evolution is a braided branching bush of multiple interacting hominins, not a single linear ladder",
    ],
  },
  {
    id: "h-holocene",
    timeWindow: "≈ 12,000 years ago to Present",
    species: "Holocene Agriculture & Global Settlements",
    significance: "Transition from mobile foraging bands to plant/animal domestication, writing, and planetary civilization.",
    level: "established",
    refs: [9],
    traits: [
      "Domestication of cereal grains and herd animals across multiple continents",
      "Rapid demographic expansion and urban social specialization",
      "Technological emergence leading to astronomy, physics, and cosmic self-reflection",
    ],
  },
];
